import {
  Component,
  OnInit,
  inject,
  runInInjectionContext,
  effect,
  Injector,
  ViewChild,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { PAGE } from './routes';
import { IonModal, Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { initializeApp, FirebaseOptions } from 'firebase/app';
import { OperatorService } from './core/services/operator/operator.service';
import { EventName } from './core/services/analytics/event-names';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { Theme, ThemeService } from './core/services/theme/theme.service';
import { InitService } from './core/services/init/init.service';
import { BootupService } from './core/services/bootup/bootup.service';
import { ActivityFeedService } from './core/services/activity-feed/activity-feed.service';
import { TranslocoService } from '@jsverse/transloco';
import { JobService } from './core/services/job/job.service';
import { Job } from './core/services/job/job';
import { getLegacySafeAreaInsets } from './shared/utils/safe-area.utils';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild(IonModal) modal!: IonModal;

  private router = inject(Router);
  private platform = inject(Platform);
  private analyticsService = inject(AnalyticsService);
  private injector = inject(Injector);
  private initService = inject(InitService);
  private bootupService = inject(BootupService);
  private activityFeedService = inject(ActivityFeedService);
  private operatorService = inject(OperatorService);
  private translocoService = inject(TranslocoService);
  private jobService = inject(JobService);

  Theme = Theme;
  themeService = inject(ThemeService);
  operatorTabOptions?: { label: string; id: string }[];
  recentMessages = signal<string[]>([]);

  constructor() {
    this.applySafeAreaInsets();
  }

  private async initialise() {
    if (!OperatorService.IsDemo) {
      initializeApp(environment.firebase as FirebaseOptions);
      this.analyticsService.initialise();
    }

    const job = this.jobService.create(async () => {
      await this.bootupService.initialise();
    });
    job.showNoInternetRetry(Job.InfiniteRetry);
    job.addErrorHandler().showFatalError().reloadWindow();
    await job.runNoThrow();

    this.analyticsService.log(EventName.FirebaseInitialised);

    // Move initService.initialise() here, after Firebase is ready
    this.initService.initialise();

    runInInjectionContext(this.injector, () => {
      effect(() => {
        const initialised = this.initService.initialised();
        if (initialised) {
          try {
            this.activityFeedService.initialize();
          } catch (error) {
            console.error(error);
          }
        }
      });
    });
  }

  async ngOnInit(): Promise<void> {
    await this.initialise();
    this.updateTranslations();
  }

  private updateTranslations(): void {
    this.translocoService
      .selectTranslate([
        'games.solitaire-name',
        'games.blocks-name',
        'games.blackjack-name',
        'games.twentyone-name',
        'tabs.play',
        'tabs.results',
        'tabs.leaderboard',
        'tabs.rules',
      ])
      .subscribe((translations) => {
        this.operatorTabOptions = [
          { label: translations[4], id: `${PAGE.GAME}/${this.operatorService.gameId}` },
          { label: translations[5], id: PAGE.RESULTS },
          { label: translations[6], id: PAGE.CHOOSE_LEADERBOARDS },
          { label: translations[7], id: `${PAGE.SELECT_RULES}/${this.operatorService.gameId}` },
        ];
      });
  }

  onCloseClicked() {
    this.operatorService.redirectToOperator(); // always redirect
  }

  get backgroundImage(): string {
    if (this.themeService.hasBackgroundImg()) {
      return `url('/assets/images/${this.themeService.currentTheme()}/background.jpg')`;
    }
    return `none`;
  }

  onThemeClicked() {
    this.modal.present();
  }

  onThemeCloseClick() {
    this.modal.dismiss();
  }

  async canDismiss(data?: unknown, role?: string) {
    return role !== 'gesture';
  }

  isMobile(): boolean {
    return this.platform.is('mobile');
  }

  onHeaderCloseClicked() {
    this.onCloseClicked();
  }

  onHeaderThemeClicked() {
    this.onThemeClicked();
  }

  onHeaderTabSelected(tabId: string) {
    this.router.navigate([tabId]);
  }

  private applySafeAreaInsets(): void {
    this.platform.ready().then(async () => {
      try {
        const legacyInsets = getLegacySafeAreaInsets();

        const urlParams = new URLSearchParams(window.location.search);
        const top = urlParams.get('insetTop') ?? legacyInsets.top;
        const bottom = urlParams.get('insetBottom') ?? legacyInsets.bottom;
        const left = urlParams.get('insetLeft') ?? legacyInsets.left;
        const right = urlParams.get('insetRight') ?? legacyInsets.right;

        const root = document.documentElement;

        root.style.setProperty('--native-safe-area-inset-top', `${top}px`);
        root.style.setProperty('--native-safe-area-inset-bottom', `${bottom}px`);
        root.style.setProperty('--native-safe-area-inset-left', `${left}px`);
        root.style.setProperty('--native-safe-area-inset-right', `${right}px`);

        root.style.setProperty('--ion-safe-area-top', `${top}px`);
        root.style.setProperty('--ion-safe-area-bottom', `${bottom}px`);
        root.style.setProperty('--ion-safe-area-left', `${left}px`);
        root.style.setProperty('--ion-safe-area-right', `${right}px`);
      } catch (error) {
        this.analyticsService.handleError(error, 'Set safe area insets');
      }
    });
  }
}
